/**
 * Menu API Service
 *
 * API client for menu endpoints.
 * Works with Python FastAPI backend.
 *
 * @since 2025-12-16
 * @epic INIT-0045, INIT-0046
 */

import { api } from './apiClient';
import {
  MenuTreeResponse,
  MenuTreeParams,
  MenuListResponse,
  MenuFilter,
  MenuType,
  MenuResponse,
} from '@/types/menu';

/**
 * Base path for menu endpoints.
 * Full path: /api/v1/content/menus
 * The /api/v1 prefix is handled by the apiClient baseURL.
 * The content module is mounted at /content (no internal v1).
 */
const BASE_PATH = '/content/menus';

/**
 * Menu API service.
 */
export const menuApi = {
  /**
   * Get menu tree (hierarchical structure).
   * This is the primary method for loading sidebar navigation.
   *
   * @param params - Tree parameters (menuTypeId, rootId, maxDepth, status)
   * @returns Menu tree response with hierarchical items
   */
  async getTree(params?: MenuTreeParams): Promise<MenuTreeResponse> {
    const queryParams = new URLSearchParams();

    if (params?.menuTypeId) {
      queryParams.append('menu_type_id', String(params.menuTypeId));
    }
    if (params?.rootId) {
      queryParams.append('root_id', String(params.rootId));
    }
    if (params?.maxDepth) {
      queryParams.append('max_depth', String(params.maxDepth));
    }
    if (params?.status !== undefined) {
      queryParams.append('status', String(params.status));
    }
    if (params?.locale) {
      queryParams.append('locale', params.locale);
    }

    const queryString = queryParams.toString();
    const url = `${BASE_PATH}/tree${queryString ? `?${queryString}` : ''}`;

    return api.get<MenuTreeResponse>(url);
  },

  /**
   * Get menu tree by type code (convenience method).
   *
   * @param typeCode - Menu type code (menu_principal, menu_top, etc)
   * @param locale - Optional locale code (e.g., 'pt_br', 'en_us')
   * @returns Menu tree response
   */
  async getTreeByCode(typeCode: string, locale?: string): Promise<MenuTreeResponse> {
    // First get the menu type by code
    const types = await this.getTypes();
    const menuType = types.find((t) => t.code === typeCode);

    if (!menuType) {
      throw new Error(`Menu type not found: ${typeCode}`);
    }

    return this.getTree({ menuTypeId: menuType.id, locale });
  },

  /**
   * Get all menu types.
   *
   * @param status - Optional status filter
   * @returns Array of menu types
   */
  async getTypes(status?: number): Promise<MenuType[]> {
    const queryParams = new URLSearchParams();

    if (status !== undefined) {
      queryParams.append('status', String(status));
    }

    const queryString = queryParams.toString();
    const url = `${BASE_PATH}/types${queryString ? `?${queryString}` : ''}`;

    return api.get<MenuType[]>(url);
  },

  /**
   * Get menu type by ID.
   *
   * @param typeId - Menu type ID
   * @returns Menu type
   */
  async getType(typeId: number): Promise<MenuType> {
    return api.get<MenuType>(`${BASE_PATH}/types/${typeId}`);
  },

  /**
   * List menus with pagination and filters.
   *
   * @param filter - Filter parameters
   * @param page - Page number (default 1)
   * @param pageSize - Items per page (default 20)
   * @returns Paginated menu list
   */
  async list(
    filter?: MenuFilter,
    page: number = 1,
    pageSize: number = 20
  ): Promise<MenuListResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', String(page));
    queryParams.append('page_size', String(pageSize));

    if (filter?.status !== undefined) {
      queryParams.append('status', String(filter.status));
    }
    if (filter?.menuTypeId) {
      queryParams.append('menu_type_id', String(filter.menuTypeId));
    }
    if (filter?.parentId) {
      queryParams.append('parent_id', String(filter.parentId));
    }
    if (filter?.search) {
      queryParams.append('search', filter.search);
    }

    const url = `${BASE_PATH}?${queryParams.toString()}`;
    return api.get<MenuListResponse>(url);
  },

  /**
   * Get menu by ID.
   *
   * @param menuId - Menu ID
   * @returns Menu details
   */
  async get(menuId: number): Promise<MenuResponse> {
    return api.get<MenuResponse>(`${BASE_PATH}/${menuId}`);
  },

  /**
   * Get menu children.
   *
   * @param menuId - Parent menu ID
   * @param status - Optional status filter
   * @returns Array of child menus
   */
  async getChildren(menuId: number, status?: number): Promise<MenuResponse[]> {
    const queryParams = new URLSearchParams();

    if (status !== undefined) {
      queryParams.append('status', String(status));
    }

    const queryString = queryParams.toString();
    const url = `${BASE_PATH}/${menuId}/children${queryString ? `?${queryString}` : ''}`;

    return api.get<MenuResponse[]>(url);
  },

  /**
   * Get menu ancestors (breadcrumb path).
   *
   * @param menuId - Menu ID
   * @returns Array of ancestor menus (root to parent)
   */
  async getAncestors(menuId: number): Promise<MenuResponse[]> {
    return api.get<MenuResponse[]>(`${BASE_PATH}/${menuId}/ancestors`);
  },

  /**
   * Get all descendants of a menu.
   *
   * @param menuId - Menu ID
   * @param maxDepth - Optional max depth
   * @returns Array of descendant menus
   */
  async getDescendants(menuId: number, maxDepth?: number): Promise<MenuResponse[]> {
    const queryParams = new URLSearchParams();

    if (maxDepth !== undefined) {
      queryParams.append('max_depth', String(maxDepth));
    }

    const queryString = queryParams.toString();
    const url = `${BASE_PATH}/${menuId}/descendants${queryString ? `?${queryString}` : ''}`;

    return api.get<MenuResponse[]>(url);
  },

  /**
   * Get menu statistics.
   *
   * @param menuId - Menu ID
   * @returns Menu statistics
   */
  async getStats(menuId: number): Promise<{
    menu_id: number;
    code: string;
    name: string;
    children_count: number;
    depth: number;
    status: number;
    menu_type_id: number;
  }> {
    return api.get(`${BASE_PATH}/${menuId}/stats`);
  },

  /**
   * Get menu count by type.
   *
   * @returns Count of menus grouped by type
   */
  async getCountByType(): Promise<Record<string, number>> {
    return api.get(`${BASE_PATH}/stats/count-by-type`);
  },

  /**
   * Get menu count by status.
   *
   * @returns Count of menus grouped by status
   */
  async getCountByStatus(): Promise<Record<string, number>> {
    return api.get(`${BASE_PATH}/stats/count-by-status`);
  },

  // ============================================================
  // CRUD Operations
  // ============================================================

  /**
   * Create a new menu.
   *
   * @param data - Menu creation data
   * @returns Created menu
   */
  async create(data: {
    label: string;
    url: string;
    icon?: string;
    target?: string;
    cssClass?: string;
    isPublic?: boolean;
    menuTypeId: number;
    parentId?: number;
    contentId?: number;
    menuOrder?: number;
    status?: number;
  }): Promise<MenuResponse> {
    return api.post<MenuResponse>(BASE_PATH, {
      label: data.label,
      url: data.url,
      icon: data.icon,
      target: data.target || '_self',
      css_class: data.cssClass,
      is_public: data.isPublic ?? true,
      menu_type_id: data.menuTypeId,
      parent_id: data.parentId,
      content_id: data.contentId,
      menu_order: data.menuOrder ?? 0,
      status: data.status ?? 1,
    });
  },

  /**
   * Update an existing menu.
   *
   * @param menuId - Menu ID
   * @param data - Menu update data
   * @returns Updated menu
   */
  async update(
    menuId: number,
    data: {
      label?: string;
      url?: string;
      icon?: string;
      target?: string;
      cssClass?: string;
      isPublic?: boolean;
      menuTypeId?: number;
      parentId?: number | null;
      contentId?: number | null;
      menuOrder?: number;
      status?: number;
    }
  ): Promise<MenuResponse> {
    const payload: Record<string, unknown> = {};

    if (data.label !== undefined) payload.label = data.label;
    if (data.url !== undefined) payload.url = data.url;
    if (data.icon !== undefined) payload.icon = data.icon;
    if (data.target !== undefined) payload.target = data.target;
    if (data.cssClass !== undefined) payload.css_class = data.cssClass;
    if (data.isPublic !== undefined) payload.is_public = data.isPublic;
    if (data.menuTypeId !== undefined) payload.menu_type_id = data.menuTypeId;
    if (data.parentId !== undefined) payload.parent_id = data.parentId;
    if (data.contentId !== undefined) payload.content_id = data.contentId;
    if (data.menuOrder !== undefined) payload.menu_order = data.menuOrder;
    if (data.status !== undefined) payload.status = data.status;

    return api.put<MenuResponse>(`${BASE_PATH}/${menuId}`, payload);
  },

  /**
   * Delete a menu.
   *
   * @param menuId - Menu ID
   * @param recursive - Delete children too
   * @returns Success message
   */
  async deleteMenu(menuId: number, recursive: boolean = false): Promise<{ message: string }> {
    const queryParams = new URLSearchParams();
    if (recursive) {
      queryParams.append('recursive', 'true');
    }
    const queryString = queryParams.toString();
    const url = `${BASE_PATH}/${menuId}${queryString ? `?${queryString}` : ''}`;

    return api.delete<{ message: string }>(url);
  },

  /**
   * Move a menu to a new parent.
   *
   * @param menuId - Menu ID to move
   * @param newParentId - New parent ID (null for root)
   * @param newOrder - New order position
   * @returns Updated menu
   */
  async move(
    menuId: number,
    newParentId: number | null,
    newOrder?: number
  ): Promise<MenuResponse> {
    const queryParams = new URLSearchParams();
    if (newParentId !== null) {
      queryParams.append('new_parent_id', String(newParentId));
    }
    if (newOrder !== undefined) {
      queryParams.append('new_order', String(newOrder));
    }
    const queryString = queryParams.toString();
    const url = `${BASE_PATH}/${menuId}/move${queryString ? `?${queryString}` : ''}`;

    return api.post<MenuResponse>(url, {});
  },

  /**
   * Reorder multiple menus.
   *
   * @param items - Array of { id, parentId, order }
   * @returns Success message
   */
  async reorder(
    items: Array<{ id: number; parentId?: number | null; order: number }>
  ): Promise<{ message: string }> {
    return api.post<{ message: string }>(`${BASE_PATH}/reorder`, {
      items: items.map((item) => ({
        id: item.id,
        parent_id: item.parentId,
        order: item.order,
      })),
    });
  },

  // ============================================================
  // Menu Type CRUD
  // ============================================================

  /**
   * Create a new menu type.
   *
   * @param data - Menu type data
   * @returns Created menu type
   */
  async createType(data: {
    code: string;
    name: string;
    status?: number;
  }): Promise<MenuType> {
    return api.post<MenuType>(`${BASE_PATH}/types`, {
      code: data.code,
      name: data.name,
      status: data.status ?? 1,
    });
  },

  /**
   * Update a menu type.
   *
   * @param typeId - Menu type ID
   * @param data - Update data
   * @returns Updated menu type
   */
  async updateType(
    typeId: number,
    data: {
      code?: string;
      name?: string;
      status?: number;
    }
  ): Promise<MenuType> {
    return api.put<MenuType>(`${BASE_PATH}/types/${typeId}`, data);
  },
};

export default menuApi;
